import { PDFJsViewer } from "@/components/ui/PDFJsViewer/PDFJsViewer";
import { usePdfEditorDocumentUrl } from "@/components/features/matter/file-repo/pdfEditorDocumentUrl";
import { downloadFile } from "@/lib/data/requests/files/canonical-files";
import { queryKeys } from "@/lib/data/static/query-keys";
import { useQuery } from "@tanstack/react-query";
import {
  ActionConfirmationModal,
  Button,
  LoadingSpinner,
  Typography,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { Plus, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import {
  clamp,
  createField,
  FIELD_CONFIG,
  SIGNER_COLORS,
  type FieldType,
  type LayoutField,
  type PendingSignerSlot,
  type SignerSlot,
} from "./SignatureRequestPdfLayoutEditor.constants";
import {
  convertSignerTemplateMarkersToFields,
  extractSignerTemplateMarkersFromPdf,
  getSignerTemplateMarkerDetails,
  type SignerTemplateMarker,
} from "./signatureRequestTemplateFields";
import {
  SignatureRequestPdfLayoutEditorDetails,
  SignatureRequestPdfLayoutEditorSignerFields,
} from "./SignatureRequestPdfLayoutEditorDetails";
import { SignatureRequestPdfLayoutEditorSidebar } from "./SignatureRequestPdfLayoutEditorSidebar";
import "./SignatureRequestPdfLayoutEditor.css";

type SignatureRequestPdfLayoutEditorProps = {
  activeSignerId: string;
  fileName: string;
  fileType: "matter-file" | "firm-file";
  fileId?: string;
  fields: LayoutField[];
  onFieldsChange: (fields: LayoutField[]) => void;
  presignedUrlKey: string;
  signers: SignerSlot[];
  onActiveSignerChange: (signerId: string) => void;
  onRequestSignerSlot: (slot: {
    index: number;
    kind: SignerSlot["kind"];
  }) => void;
  onPendingSignerSlotsChange: (
    slots: PendingSignerSlot[],
    templateSlots: PendingSignerSlot[],
  ) => void;
};

export function SignatureRequestPdfLayoutEditor({
  activeSignerId,
  fileName,
  fileType,
  fileId,
  fields,
  onFieldsChange,
  presignedUrlKey,
  signers,
  onActiveSignerChange,
  onRequestSignerSlot,
  onPendingSignerSlotsChange,
}: SignatureRequestPdfLayoutEditorProps) {
  const [selectedFieldId, setSelectedFieldId] = useState<string>("");
  const [scrollToPage, setScrollToPage] = useState<
    ((pageNumber: number) => void) | null
  >(null);
  const [isResetFieldsConfirmOpen, setIsResetFieldsConfirmOpen] =
    useState(false);
  const [isDraggingField, setIsDraggingField] = useState(false);
  const [templateMarkers, setTemplateMarkers] = useState<
    SignerTemplateMarker[]
  >([]);
  const autoConvertedDocumentRef = useRef("");
  const [dragOffset, setDragOffset] = useState<{
    fieldId: string;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const currentDownload = useQuery({
    queryKey: [queryKeys.files, fileId, "download", presignedUrlKey],
    queryFn: () => {
      if (!fileId) throw new Error("A file must be selected.");
      return downloadFile(fileId);
    },
    enabled: !!fileId,
    staleTime: 0,
    gcTime: 0,
  });
  const {
    isErrorFileUrl,
    isLoadingFileUrl,
    isPreparingDocumentUrl,
    resolvedDocUrl,
  } = usePdfEditorDocumentUrl({
    fileType,
    initialDocUrl: fileId ? currentDownload.data?.url : undefined,
    presignedUrlKey: fileId ? undefined : presignedUrlKey,
  });

  useEffect(() => {
    const documentKey = `${fileName}:${resolvedDocUrl}:${signers
      .map((signer) => `${signer.kind}:${signer.id}`)
      .join(",")}`;
    if (!resolvedDocUrl || autoConvertedDocumentRef.current === documentKey) {
      return;
    }

    let isActive = true;

    void fetch(resolvedDocUrl)
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const markers = await extractSignerTemplateMarkersFromPdf(
          await response.arrayBuffer(),
        );
        if (!isActive) return;
        autoConvertedDocumentRef.current = documentKey;

        const convertedFields = convertSignerTemplateMarkersToFields(
          markers,
          signers,
        );
        setTemplateMarkers(markers);
        if (markers.length === 0) return;
        const missingFields = convertedFields.filter(
          (convertedField) =>
            !fields.some(
              (field) =>
                field.page === convertedField.page &&
                field.signerId === convertedField.signerId &&
                field.type === convertedField.type &&
                Math.abs(field.x - convertedField.x) < 0.01 &&
                Math.abs(field.y - convertedField.y) < 0.01,
            ),
        );
        if (missingFields.length > 0) {
          onFieldsChange([...fields, ...missingFields]);
        }
      })
      .catch((error: unknown) => {
        console.error("Failed to convert signer template fields", error);
      });

    return () => {
      isActive = false;
    };
  }, [
    fields,
    fileName,
    onFieldsChange,
    presignedUrlKey,
    resolvedDocUrl,
    signers,
  ]);

  const selectedField = useMemo(
    () => fields.find((field) => field.id === selectedFieldId) ?? null,
    [fields, selectedFieldId],
  );
  const activeSignerOrder = useMemo(
    () => signers.findIndex((signer) => signer.id === activeSignerId) + 1,
    [activeSignerId, signers],
  );
  const pendingTemplateFields = useMemo(
    () =>
      templateMarkers.flatMap((marker) => {
        const details = getSignerTemplateMarkerDetails(marker.variable);
        if (!details) return [];

        const signerPrefix =
          details.signerKind === "user" ? "UserSigner" : "Signer";
        const signer = signers.find(
          (item) =>
            item.kind === details.signerKind &&
            item.label === `${signerPrefix}${details.signerIndex + 1}`,
        );
        if (signer) return [];

        return [{ details, marker }];
      }),
    [signers, templateMarkers],
  );
  const pendingSignerSlots = useMemo<PendingSignerSlot[]>(() => {
    const slots = new Map<string, PendingSignerSlot>();

    pendingTemplateFields.forEach(({ details }) => {
      const key = `${details.signerKind}:${details.signerIndex}`;
      const current = slots.get(key);
      slots.set(key, {
        fieldCount: (current?.fieldCount ?? 0) + 1,
        index: details.signerIndex,
        kind: details.signerKind,
      });
    });

    return [...slots.values()];
  }, [pendingTemplateFields]);

  useEffect(() => {
    const templateSlots = templateMarkers.flatMap((marker) => {
      const details = getSignerTemplateMarkerDetails(marker.variable);
      return details
        ? [
            {
              kind: details.signerKind,
              index: details.signerIndex,
              fieldCount: 1,
            },
          ]
        : [];
    });
    onPendingSignerSlotsChange(pendingSignerSlots, templateSlots);
  }, [onPendingSignerSlotsChange, pendingSignerSlots, templateMarkers]);

  useEffect(() => {
    if (!selectedFieldId && fields.length > 0) {
      setSelectedFieldId(fields[0].id);
      return;
    }

    if (
      selectedFieldId &&
      !fields.some((field) => field.id === selectedFieldId)
    ) {
      setSelectedFieldId(fields[0]?.id ?? "");
    }
  }, [fields, selectedFieldId]);

  const beginPaletteDrag = (
    event: DragEvent<HTMLButtonElement>,
    type: FieldType,
  ) => {
    event.dataTransfer.setData("application/x-field-type", type);
    event.dataTransfer.effectAllowed = "copy";
  };

  const beginFieldDrag = (
    event: DragEvent<HTMLButtonElement>,
    fieldId: string,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.dataTransfer.setData("application/x-field-id", fieldId);
    event.dataTransfer.effectAllowed = "move";
    setDragOffset({
      fieldId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    });
    setIsDraggingField(true);
  };

  const handlePageDrop = (event: DragEvent<HTMLDivElement>, page: number) => {
    event.preventDefault();

    const pageRect = event.currentTarget.getBoundingClientRect();
    const pageWidth = Math.max(pageRect.width, 1);
    const pageHeight = Math.max(pageRect.height, 1);
    const paletteType = event.dataTransfer.getData("application/x-field-type");
    const movedFieldId = event.dataTransfer.getData("application/x-field-id");

    if (movedFieldId) {
      const movedField = fields.find((field) => field.id === movedFieldId);
      const movedFieldOffset =
        dragOffset?.fieldId === movedFieldId ? dragOffset : null;

      onFieldsChange(
        fields.map((field) =>
          field.id !== movedFieldId
            ? field
            : {
                ...field,
                page,
                x: clamp(
                  movedFieldOffset
                    ? (event.clientX -
                        pageRect.left -
                        movedFieldOffset.offsetX) /
                        pageWidth
                    : (event.clientX - pageRect.left) / pageWidth -
                        field.width / 2,
                  0,
                  1 - field.width,
                ),
                y: clamp(
                  movedFieldOffset
                    ? (event.clientY -
                        pageRect.top -
                        movedFieldOffset.offsetY) /
                        pageHeight
                    : (event.clientY - pageRect.top) / pageHeight -
                        field.height / 2,
                  0,
                  1 - field.height,
                ),
              },
        ),
      );
      setSelectedFieldId(movedFieldId);
      if (movedField) {
        onActiveSignerChange(movedField.signerId);
      }
      setIsDraggingField(false);
      setDragOffset(null);
      return;
    }

    if (!(paletteType in FIELD_CONFIG)) return;

    const fieldType = paletteType as FieldType;
    const config = FIELD_CONFIG[fieldType];
    const sizeOverride = getLastFieldSizeForSignerAndType(
      fieldType,
      activeSignerId,
    );
    const fieldWidth = sizeOverride?.width ?? config.size[0];
    const fieldHeight = sizeOverride?.height ?? config.size[1];
    const nextField = createField(
      fieldType,
      activeSignerId,
      activeSignerOrder > 0 ? activeSignerOrder : undefined,
      page,
      clamp(
        (event.clientX - pageRect.left) / pageWidth - fieldWidth / 2,
        0,
        1 - fieldWidth,
      ),
      clamp(
        (event.clientY - pageRect.top) / pageHeight - fieldHeight / 2,
        0,
        1 - fieldHeight,
      ),
      sizeOverride,
    );
    onFieldsChange([...fields, nextField]);
    setSelectedFieldId(nextField.id);
    setIsDraggingField(false);
    setDragOffset(null);
  };

  const updateField = (
    fieldId: string,
    updates: Partial<
      Pick<
        LayoutField,
        | "height"
        | "label"
        | "page"
        | "signerId"
        | "width"
        | "fontFamily"
        | "fontSize"
      >
    >,
  ) => {
    onFieldsChange(
      fields.map((field) =>
        field.id === fieldId ? { ...field, ...updates } : field,
      ),
    );
  };

  const getLastFieldSizeForSignerAndType = (
    type: FieldType,
    signerId: string,
  ) => {
    for (let index = fields.length - 1; index >= 0; index -= 1) {
      const field = fields[index];
      if (field.type === type && field.signerId === signerId) {
        return { height: field.height, width: field.width };
      }
    }

    return undefined;
  };

  const removeField = (fieldId: string) => {
    onFieldsChange(fields.filter((field) => field.id !== fieldId));
    setSelectedFieldId((current) => (current === fieldId ? "" : current));
  };

  const resetFields = () => {
    onFieldsChange([]);
    setSelectedFieldId("");
    setIsResetFieldsConfirmOpen(false);
  };

  const selectField = (fieldId: string) => {
    const field = fields.find((item) => item.id === fieldId);
    if (!field) return;

    setSelectedFieldId(fieldId);
    onActiveSignerChange(field.signerId);
    scrollToPage?.(field.page);
  };

  if (currentDownload.isLoading || isLoadingFileUrl || isPreparingDocumentUrl) {
    return (
      <div className="border-border bg-card flex min-h-[560px] items-center justify-center gap-3 rounded-xl border">
        <LoadingSpinner />
        <Typography className="text-muted-foreground text-sm">
          Preparing generated PDF…
        </Typography>
      </div>
    );
  }

  if (!resolvedDocUrl || currentDownload.isError || isErrorFileUrl) {
    return (
      <div className="border-border bg-card flex min-h-[560px] items-center justify-center rounded-xl border p-6 text-center">
        <Typography className="text-muted-foreground text-sm">
          Unable to load the generated PDF preview.
        </Typography>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 w-full overflow-x-auto overflow-y-hidden">
      <div className="grid h-full min-h-0 w-full min-w-[800px] grid-cols-[minmax(0,1fr)_300px] gap-0">
        <section className="bg-card flex min-h-0 min-w-0 flex-col overflow-hidden border-r border-l">
          <div className="border-border/70 flex items-center justify-between gap-4 border-b px-4 py-3.5">
            <Typography variant="heading" size="sm">
              Document
            </Typography>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={fields.length === 0}
              onClick={() => {
                setIsResetFieldsConfirmOpen(true);
              }}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset fields
            </Button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-100/40 p-3">
            <PDFJsViewer
              src={resolvedDocUrl}
              className="min-h-0 flex-1 overflow-hidden rounded-lg bg-slate-50"
              contentClassName="min-h-0 flex-1 overflow-auto p-2 [&>*]:mb-2 [&>*:last-child]:mb-0"
              pageShellClassName="border-slate-200 shadow-none"
              onReady={({ scrollToPage: nextScrollToPage }) => {
                setScrollToPage(() => nextScrollToPage);
              }}
              renderPageOverlay={({ pageNumber }) => (
                <div
                  className="absolute inset-0"
                  onDragOver={(event) => {
                    event.preventDefault();
                  }}
                  onDrop={(event) => {
                    handlePageDrop(event, pageNumber);
                  }}
                >
                  {templateMarkers
                    .filter((marker) => marker.page === pageNumber)
                    .map((marker) => {
                      const padding = 0.003;

                      return (
                        <div
                          key={`${marker.variable}:${marker.x}:${marker.y}`}
                          aria-hidden
                          className="pointer-events-none absolute bg-white"
                          style={{
                            left: `${clamp(marker.x - padding, 0, 1) * 100}%`,
                            top: `${clamp(marker.y - padding, 0, 1) * 100}%`,
                            width: `${clamp(marker.width + padding * 2, 0, 1) * 100}%`,
                            height: `${clamp(marker.height + padding * 2, 0, 1) * 100}%`,
                          }}
                        />
                      );
                    })}
                  {pendingTemplateFields
                    .filter(({ marker }) => marker.page === pageNumber)
                    .map(({ details, marker }) => {
                      const [defaultWidth, defaultHeight] =
                        FIELD_CONFIG[details.fieldType].size;
                      const width = details.sizeOverride?.width ?? defaultWidth;
                      const height =
                        details.sizeOverride?.height ?? defaultHeight;
                      const signerLabel =
                        details.signerKind === "user"
                          ? "Firm signer"
                          : "Signer";

                      return (
                        <button
                          type="button"
                          key={`pending:${marker.variable}:${marker.x}:${marker.y}`}
                          aria-label={`${details.fieldLabel}, ${signerLabel} ${details.signerIndex + 1} not added`}
                          title={`${details.fieldLabel} — Add ${signerLabel.toLowerCase()} ${details.signerIndex + 1}`}
                          onClick={() => {
                            onRequestSignerSlot({
                              index: details.signerIndex,
                              kind: details.signerKind,
                            });
                          }}
                          className="signer-layout-field group absolute gap-1 rounded-[4px] border border-slate-400 bg-slate-100/80 text-left shadow-sm transition-colors hover:border-slate-600 hover:bg-slate-200"
                          style={{
                            left: `${clamp(marker.x, 0, 1 - width) * 100}%`,
                            top: `${clamp(marker.y + marker.height - height, 0, 1 - height) * 100}%`,
                            width: `${width * 100}%`,
                            height: `${height * 100}%`,
                          }}
                        >
                          <Plus className="h-3 w-3 shrink-0 text-slate-600" />
                          <span className="signer-layout-field-label text-slate-600">
                            {details.fieldLabel}
                          </span>
                        </button>
                      );
                    })}
                  {fields
                    .filter((field) => field.page === pageNumber)
                    .map((field) => {
                      const isSelected = field.id === selectedFieldId;
                      const colorIndex = signers.findIndex(
                        (signer) => signer.id === field.signerId,
                      );
                      const color =
                        SIGNER_COLORS[
                          (signers[colorIndex]?.colorIndex ??
                            Math.max(0, colorIndex)) % SIGNER_COLORS.length
                        ];
                      const signer = signers.find(
                        (item) => item.id === field.signerId,
                      );
                      const fieldLabel =
                        field.label &&
                        field.label.trim().toLowerCase() ===
                          FIELD_CONFIG[field.type].label.toLowerCase()
                          ? field.label
                          : field.label
                            ? `${field.label} (${FIELD_CONFIG[field.type].label})`
                            : FIELD_CONFIG[field.type].label;
                      const signerName =
                        signer?.displayName || signer?.slotName;

                      return (
                        <button
                          key={field.id}
                          type="button"
                          aria-label={
                            signerName
                              ? `${fieldLabel}, ${signerName}`
                              : fieldLabel
                          }
                          title={
                            signerName
                              ? `${fieldLabel} — ${signerName}`
                              : fieldLabel
                          }
                          draggable
                          onClick={() => {
                            selectField(field.id);
                          }}
                          onDragStart={(event) => {
                            beginFieldDrag(event, field.id);
                          }}
                          onDragEnd={() => {
                            setIsDraggingField(false);
                            setDragOffset(null);
                          }}
                          className={cn(
                            "signer-layout-field absolute rounded-[4px] border text-left shadow-sm transition-[background-color,border-color,box-shadow,transform] duration-150",
                            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                            isSelected && "scale-[1.01] shadow-lg",
                          )}
                          style={{
                            left: `${field.x * 100}%`,
                            top: `${field.y * 100}%`,
                            width: `${field.width * 100}%`,
                            height: `${field.height * 100}%`,
                            borderColor: isSelected
                              ? color.selectedBorder
                              : color.border,
                            backgroundColor: isSelected
                              ? color.selectedBg
                              : color.bg,
                            boxShadow: isSelected
                              ? `0 0 0 4px ${color.selectedBorder}40`
                              : undefined,
                          }}
                        >
                          <span
                            className={cn(
                              "signer-layout-field-label",
                              color.text,
                            )}
                          >
                            {fieldLabel}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}
            />
          </div>
        </section>

        <SignatureRequestPdfLayoutEditorSidebar
          activeSignerId={activeSignerId}
          onBeginPaletteDrag={beginPaletteDrag}
          signers={signers}
          fieldPlacementDisabled={signers.length === 0}
          signerFields={
            <SignatureRequestPdfLayoutEditorSignerFields
              fields={fields}
              activeSignerId={activeSignerId}
              fieldDetails={
                <SignatureRequestPdfLayoutEditorDetails
                  isDraggingField={isDraggingField}
                  onRemoveField={removeField}
                  onUpdateField={updateField}
                  selectedField={selectedField}
                />
              }
              onSelectFieldId={selectField}
              selectedField={selectedField}
            />
          }
        />
      </div>

      <ActionConfirmationModal
        open={isResetFieldsConfirmOpen}
        setOpen={setIsResetFieldsConfirmOpen}
        title="Reset fields?"
        description="This will remove every signature field from the document. This cannot be undone."
        buttonText="Reset fields"
        actionVariant="destructive"
        afterActionFn={resetFields}
      />
    </div>
  );
}
